import { useNavigate } from 'react-router-dom';
import { LogOut, ChevronDown, User, Menu } from 'lucide-react';
import { getInitials } from 'utils/helpers';
import { Avatar, Dropdown } from 'react-daisyui';
import { getPatient, getPractice } from 'library/auth';
import { PORTAL_ROUTES } from 'utils/constants';

export const Header = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const navigate = useNavigate();
  const patient = getPatient();

  const handleSignOut = () => {
    navigate('/sign-out', { replace: true });
  };

  const practiceInfo = getPractice();

  const fullName = patient.full_name || 'Patient';


  return (
    <header className="w-full bg-transparent sticky top-0 z-30">
      <div className="h-16 pl-3 pr-3 md:px-8 flex items-center justify-between">
        {/* Left: Practice logo + name */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={onMenuClick}
            className="md:hidden w-9 h-9 flex items-center justify-center text-secondary-500 hover:bg-gray-100 rounded-xl transition-colors mr-2"
          >
            <Menu size={22} />
          </button>
          <button
            onClick={() => navigate(PORTAL_ROUTES.DASHBOARD)}
            className="flex items-center gap-2.5 rounded-xl hover:opacity-80 transition-opacity"
          >
            {practiceInfo.logo ? (
              <Avatar size="xs" src={practiceInfo.logo} shape="circle" />
            ) : (
              <Avatar size="xs" src="/logo.png" />
            )}
            <span className="text-xl font-bold text-secondary-900 tracking-tight">
              {practiceInfo.title || 'Patient Portal'}
            </span>
          </button>
        </div>

        {/* Right: Profile */}
        <div className="flex items-center gap-3">
          {/* Profile dropdown */}
          <Dropdown end className="relative">
            <Dropdown.Toggle
              button={false}
              aria-haspopup="menu"
              role="button"
              className="flex items-center gap-2.5 py-1.5 pl-1.5 pr-2 rounded-full hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-teamcare-blue text-white flex items-center justify-center text-sm font-bold">
                {getInitials(fullName)}
              </div>
              <span className="text-sm font-semibold text-secondary-800 hidden sm:inline">
                {fullName}
              </span>
              <ChevronDown
                size={14}
                className="text-secondary-400"
              />
            </Dropdown.Toggle>

            <Dropdown.Menu
              className="w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-scale-in"
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-bold text-secondary-900">{fullName}</p>
                {patient.email && (
                  <p className="text-xs text-secondary-400 mt-0.5">{patient.email}</p>
                )}
              </div>
              <div className="py-1">
                <li role="menuitem">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-700 hover:!bg-gray-50 focus:!bg-gray-50 focus:!text-secondary-700 active:!bg-gray-50 active:!text-secondary-700 transition-colors"
                  >
                    <User size={16} className="text-secondary-400" />
                    My Profile
                  </button>
                </li>
              </div>
              <div className="border-t border-gray-100 pt-1">
                <li role="menuitem">
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:!bg-red-50 focus:!bg-red-50 focus:!text-red-500 active:!bg-red-50 active:!text-red-500 transition-colors"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </li>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
